/**
 * Cable profiles -- group 00 (reference exemplars).
 *
 * One entry per cable type so the pattern is clear for every category.
 *
 * The rule for this whole directory: state construction facts, and let the kit
 * derive the electrical figures unless the manufacturer actually publishes
 * them. Most do not. Where a maker does publish resistance, inductance or
 * capacitance -- Belden, Canare, Mogami, Supra, Blue Jeans and the pro-audio
 * houses generally do -- pass the real numbers and mark them MANUFACTURER_SPEC.
 *
 * Do NOT write notes claiming a cable makes anything "warmer", "blacker" or
 * "more open". The engine cannot model that and this database does not assert
 * it. Describe the construction and, where it matters, the electrical
 * consequence.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_00: CableProfile[] = [
  // ---- Speaker -----------------------------------------------------------
  makeCable({
    mfr: 'Kimber Kable',
    model: '8TC',
    cableType: 'speaker',
    price: usd(560, 'CURRENT_RETAIL', 2024, 'Price for a 3 m terminated pair.'),
    priceLengthM: 3,
    year: 1979,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 11,
    // Kimber publishes these, and the braid is the reason the inductance is low.
    resistanceOhmPerM: 0.0082,
    inductanceUhPerM: 0.19,
    capacitancePfPerM: 240,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Kimber Kable published figures for 8TC',
    dielectric: 'Teflon (FEP)',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c9a227',
    tags: ['braided', 'low-inductance', 'classic'],
    notes:
      'Eight conductors woven so that adjacent runs carry opposing current, which cancels much of the external field and drops loop inductance to roughly a third of an ordinary twisted pair. The trade is capacitance an order of magnitude higher than a simple zip cord, which is harmless into a loudspeaker but would matter if anyone used it as an interconnect.',
  }),

  makeCable({
    mfr: 'Canare',
    model: '4S11',
    cableType: 'speaker',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 11,
    resistanceOhmPerM: 0.0084,
    inductanceUhPerM: 0.19,
    capacitancePfPerM: 165,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Canare 4S11 datasheet',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6ea8ff',
    tags: ['star-quad', 'pro-audio', 'published-specs'],
    notes:
      'Star-quad with the four conductors paralleled in opposing pairs. Electrically almost indistinguishable from the braided designs at many times the price, and it is one of the few speaker cables whose full R, L and C figures are on a public datasheet -- which is why it sits here as the reference point.',
  }),

  // ---- Interconnect ------------------------------------------------------
  makeCable({
    mfr: 'Mogami',
    model: 'W2549 Neglex',
    cableType: 'interconnect',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    capacitancePfPerM: 62,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Mogami W2549 datasheet',
    shielding: 'Spiral copper shield, 100% coverage',
    termination: ['XLR', 'RCA'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#45c8b8',
    tags: ['balanced', 'studio-standard', 'published-specs'],
    notes:
      'The studio default. 62 pF/m means a 1 m run presents about 62 pF, which against even a 600 ohm tube output stage puts the low-pass corner above 4 MHz. The engine computes that and reports the in-band effect, which is nil to two decimal places.',
  }),

  // ---- Power -------------------------------------------------------------
  makeCable({
    mfr: 'Shunyata Research',
    model: 'Venom NR-V10',
    cableType: 'power',
    price: usd(495, 'MSRP', 2024, 'Price for a 1.75 m cord.'),
    priceLengthM: 1.75,
    year: 2019,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Shielded, with the maker\'s noise-reduction filtering',
    termination: ['IEC C15', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b48ead',
    tags: ['shielded', 'high-current'],
    notes:
      'A 12 AWG shielded cord with the maker\'s filtering in the moulding. Gauge and current rating are real and matter for a power amplifier drawing large transient currents. The engine applies NO signal processing for power cords -- see the rationale in the inspector -- so selecting this changes the chain description and the price, and leaves the audio bit-identical.',
  }),

  // ---- USB ---------------------------------------------------------------
  makeCable({
    mfr: 'Belden',
    model: 'USB 2.0 High Speed',
    cableType: 'usb',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5bc0eb',
    tags: ['usb-2.0', 'compliant', 'published-specs'],
    notes:
      'A compliant 90 ohm USB 2.0 assembly. USB audio is packetised and error-checked, and in an asynchronous DAC the clock is generated at the DAC rather than recovered from the cable, so a compliant cable either carries the data or fails to. The engine applies no signal processing here.',
  }),

  // ---- Ethernet ----------------------------------------------------------
  makeCable({
    mfr: 'Supra Cables',
    model: 'CAT8',
    cableType: 'ethernet',
    price: usd(75, 'CURRENT_RETAIL', 2024, 'Price for a 2 m patch lead.'),
    priceLengthM: 2,
    country: 'Sweden',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 8, 2 GHz certified',
    shielding: 'S/FTP, individually foiled pairs in an overall braid',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3ddc97',
    tags: ['cat8', 'shielded', 'published-specs'],
    notes:
      'Fully shielded Cat 8. Ethernet is packetised, checksummed and retransmitted on error, and a streamer buffers seconds of audio ahead of playback, so there is no analog path between the two ends. The engine applies no signal processing here.',
  }),
];
